import axios from "axios";
import { msalInstance, initializeMsal } from "../auth";
import {
  HEEDIFY_POWERBI_SERVICES_PATH,
  HEEDIFY_PROVISIONING_SERVICES_PATH,
} from "../config/config";

export const saveEmbedToken = (token) => ({
  type: "SAVE_EMBED_TOKEN",
  payload: { token },
});

export const saveReportInfos = (infos) => ({
  type: "SAVE_REPORT_INFOS",
  payload: { infos },
});

export const saveUserInfos = (userInfos) => ({
  type: "SAVE_USER_INFOS",
  payload: { userInfos },
});

export const saveUserPhoto = (userPhoto) => ({
  type: "SAVE_USER_PHOTO",
  payload: { userPhoto },
});

export const setReportsList = (list) => ({
  type: "SET_REPORTS_LIST",
  payload: { list },
});

export const updateReport = (report) => ({
  type: "UPDATE_REPORT",
  report,
});

// ===== Helper =====
async function ensureMSALInitialized() {
  await initializeMsal();
}

// ===== Helper: get access token =====
async function getAccessToken() {
  await ensureMSALInitialized();
  const account = msalInstance.getActiveAccount();

  if (!account) {
    // Trigger redirect login (will reload page)
    msalInstance.loginRedirect({
      scopes: ["User.Read"],
    });
    return null;
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: ["User.Read"],
      account,
    });
    return response.accessToken;
  } catch (error) {
    console.warn("Silent token acquisition failed, redirecting to login:", error);
    msalInstance.loginRedirect({
      scopes: ["User.Read"],
    });
    return null;
  }
}

// ===== Async action: request embed token =====
export const requestEmbedToken = () => async (dispatch) => {
  await ensureMSALInitialized();
  const account = msalInstance.getActiveAccount();

  if (!account) {
    msalInstance.loginRedirect({
      scopes: ["User.Read"],
    });
    return;
  }

  try {
    const res = await axios.get(
      `${HEEDIFY_PROVISIONING_SERVICES_PATH}/CallFlowManagement/${account.tenantId}/default/get/ALL?userid=${account.localAccountId}`
    );

    const reportKey = Object.keys(res.data)[0];
    const response = await axios.post(
      `${HEEDIFY_POWERBI_SERVICES_PATH}GetReportAccessToken`,
      {
        reportId: res.data[reportKey].reportId,
        workspaceId: res.data[reportKey].workspaceId,
      }
    );

    const tenantQueues = res.data.tenantQueues;
    dispatch(saveEmbedToken(response.data.token));
    dispatch(saveReportInfos({ ...response.data.reportInfos, tenantQueues }));
  } catch (error) {
    console.error("Error fetching embed token:", error);
  }
};

// ===== Async action: get reports list =====
export const getReportsList = () => async (dispatch) => {
  await ensureMSALInitialized();
  const account = msalInstance.getActiveAccount();

  if (!account) {
    msalInstance.loginRedirect({
      scopes: ["User.Read"],
    });
    return;
  }

  const token = await getAccessToken();
  if (!token) return;

  let callflowsPromises = [];

  try {
    const scopesRes = await axios.get(
      `${HEEDIFY_PROVISIONING_SERVICES_PATH}ScopeManagement/${account.tenantId}/get/ALL?token=${token}`
    );

    for (const scope of Object.keys(scopesRes.data)) {
      callflowsPromises.push(getCallFlow(scope, "ALL", account.localAccountId, account.tenantId));
    }
  } catch (error) {
    console.log("No scopes:", error.message);
  }

  callflowsPromises.push(getCallFlow("No_scope", "ALL", account.localAccountId, account.tenantId));

  const callflowsResults = await Promise.all(callflowsPromises);

  const callflows = callflowsResults.reduce((result, current) => {
    Object.keys(current).forEach((key) => {
      result[key] = current[key];
    });
    return result;
  }, {});

  const reportsListPromises = Object.keys(callflows).map((elm) =>
    axios
      .post(`${HEEDIFY_POWERBI_SERVICES_PATH}GetReportAccessToken`, {
        reportId: callflows[elm].reportId,
        workspaceId: callflows[elm].workspaceId,
      })
      .then((res) => {
        res.data.flowConfig = callflows[elm];
        return res.data;
      })
  );

  const reportsListData = (await Promise.all(reportsListPromises)).filter(
    (element) => Object.keys(element).length !== 0
  );

  dispatch(setReportsList(reportsListData));
};

// ===== Helper: get call flow =====
function getCallFlow(scope, action, userId, tenantId) {
  return new Promise((resolve) => {
    const url = `${HEEDIFY_PROVISIONING_SERVICES_PATH}CallFlowManagement/${tenantId}/${scope}/get/${action}?userid=${userId}`;
    axios
      .get(url)
      .then((res) => resolve(res.data || {}))
      .catch(() => resolve({}));
  });
}
